/**
 * What the projects page reads out of the payloads it is given.
 *
 * Everything here is a **pure function over rows the page is already showing** —
 * a filter, a lookup, or the one derivation DESIGN.md licenses. There is no fetch,
 * no clock of its own and no formatting: the page hands in the rows and today's
 * date, and gets back `Money`, a milestone, or a list.
 *
 * **The derivation, and why it is one.** `client/projects.html` opens on
 * *"Still to invoice — $2,685.00"*, and no read model carries that figure:
 * `GET /api/clients/me/projects` answers a work order and its milestones and
 * nothing that has been added up. DESIGN.md §Permitted derivation: "a **subtotal
 * of rows that are visible on the same page** (including inside the `<details>`
 * that the subtotal summarises) is a UI computation, not an invented fact… It must
 * reconcile exactly, and its components must remain reachable." Both components
 * are on the page: the milestone table prints every milestone's own amount, and
 * the scope-change paragraph prints the extra's total and its reference. The
 * arithmetic itself is `packages/shared/src/money.ts` and never a `+` here
 * (`.docs/00-index.md` §5).
 *
 * **A scope change is counted once.** `SERVICES.md` §Projects bills it on the
 * project's **final** milestone, so it is still to invoice only while that
 * milestone is — once the final invoice exists, the extra is already on it and
 * adding it again would double the figure.
 *
 * **A milestone finds its invoice by `(work_order_ref, milestone_seq)`**, the pair
 * `InvoiceSchema` carries for exactly this ("The project or extra this invoice is
 * for"). `MilestoneView.invoice_id` is a UUID and a client is never shown one.
 */

import type {
  ClientCatalog,
  ClientProjectMilestoneView,
  ComputedPrice,
  Currency,
  InvoiceView,
  MilestoneView,
  Money,
  WorkOrderView,
} from '@fareast/shared'
import { isBillableScopeChange, sum } from '@fareast/shared'

/**
 * One row of `GET /api/clients/me/projects`.
 *
 * The milestones are `ClientProjectMilestoneView` and not `MilestoneView` since
 * session 41: that route answers through `readmodels/client-projects.ts`, which adds
 * `late` to the ones the studio owes. Every function below takes the narrower
 * `MilestoneView` where `late` is none of its business, so the extra field reaches
 * exactly one reader — the Delivered column of the table.
 */
export type ProjectRow = {
  readonly work_order: WorkOrderView
  readonly milestones: readonly ClientProjectMilestoneView[]
}

/* ------------------------------------------------------------------ the three lists */

/**
 * Work under way — the projects that get a region of their own.
 *
 * Written as the two states a project is *in* rather than as the ones to skip,
 * because `workOrderMachine.states` gets longer and a list of exclusions is how a
 * new state silently lands in the wrong section (`CLAUDE.md` §Gotchas).
 */
export function isLive(project: ProjectRow): boolean {
  const { status } = project.work_order
  return status === 'approved' || status === 'in_progress'
}

/** Waiting on the client. The quote has been sent and nothing has been decided. */
export function isAwaiting(project: ProjectRow): boolean {
  return project.work_order.status === 'proposed'
}

/** Finished. `billed` is `delivered` with its last invoice out. */
export function isFinished(project: ProjectRow): boolean {
  const { status } = project.work_order
  return status === 'delivered' || status === 'billed'
}

/** Neither happening nor going to. Only the `All` view shows these. */
export function isClosed(project: ProjectRow): boolean {
  const { status } = project.work_order
  return status === 'declined' || status === 'cancelled'
}

/* ---------------------------------------------------------------------- milestones */

/**
 * The milestone being worked on: the first that has not been delivered.
 *
 * Milestones are delivered in order (`modules/work_orders/service.ts`
 * `deliverMilestone`), so "the first undelivered one" is the whole rule.
 */
export function currentMilestone(project: ProjectRow): MilestoneView | null {
  return project.milestones.find((one) => one.delivered_at === null) ?? null
}

/** The ones that are done, in order. */
export function deliveredMilestones(project: ProjectRow): readonly MilestoneView[] {
  return project.milestones.filter((one) => one.delivered_at !== null)
}

/** Which milestone carries the project's approved scope changes — the last one. */
export function scopeChangeMilestone(project: ProjectRow): MilestoneView | null {
  return project.milestones.find((one) => one.includes_scope_changes) ?? null
}

/* ------------------------------------------------------------------------ invoices */

/** The invoice issued for one milestone, or null while none has been. */
export function invoiceForMilestone(
  invoices: readonly InvoiceView[],
  project: ProjectRow,
  milestone: MilestoneView,
): InvoiceView | null {
  return (
    invoices.find(
      (one) => one.work_order_ref === project.work_order.ref && one.milestone_seq === milestone.seq,
    ) ?? null
  )
}

/** Every invoice this project has produced, milestone or otherwise. */
export function invoicesForProject(
  invoices: readonly InvoiceView[],
  project: ProjectRow,
): readonly InvoiceView[] {
  return invoices.filter((one) => one.work_order_ref === project.work_order.ref)
}

/* -------------------------------------------------------------------- scope changes */

/**
 * The approved extras raised against this project that will reach an invoice.
 *
 * A scope change is "an extra with a `parent_ref`" (`WorkOrderSchema`). Declined and
 * cancelled ones are not scope changes; they are things that did not happen. Neither
 * is a **waived** one: `service.waive` leaves the extra `delivered` with
 * `not_billed: true` so the value ledger still counts the work, and session 14 copies
 * it on to the invoice **at zero** — so counting its `total_amount` towards "still to
 * invoice" or "project to date" would put money in the hero that nobody will ever be
 * asked for. `REQ-116` in the recorded world is exactly that row.
 *
 * The rows come from `GET /api/clients/me/projects/:ref`'s `scope_changes` where that
 * read has answered, and from the requests ledger where it has not — see
 * `LiveProject.tsx`. Either way the filter is this one, so the two sources cannot
 * disagree about what counts.
 */
export function scopeChangesOf(
  requests: readonly WorkOrderView[],
  project: ProjectRow,
): readonly WorkOrderView[] {
  return requests.filter((one) => isBillableScopeChange(one, project.work_order.ref))
}

/* ------------------------------------------------------------------ the template */

/**
 * What the mockup's `<span class="tag">` beside a reference says — "Business site, up
 * to 6 pages", "Discovery, fixed".
 *
 * `work_order.template` is a **code** (`website_m`), and the name that goes with it is
 * the rate card's: `GET /api/catalog` → `projects[].sizes[].name`. Resolved here
 * rather than on the page because both the live region and the delivered card print
 * it, and null — an unpriced work order, a template the card no longer carries, or a
 * catalog read that has not landed — means the tag is simply not drawn.
 */
export function templateNameOf(catalog: ClientCatalog | undefined, code: string | null) {
  if (catalog === undefined || code === null) return null
  for (const service of catalog.projects) {
    const size = service.sizes.find((one) => one.code === code)
    if (size !== undefined) return size.name
  }
  return null
}

/* --------------------------------------------------------------------- the quote */

/** The build-up behind a project quote, as `pricing_breakdown` stored it. */
export type ProjectBreakdownView = NonNullable<ComputedPrice['project']>

/**
 * The project build-up out of `WorkOrderDetail.pricing`.
 *
 * `pricing` is "one entry for a project, one per unit for an extra, and empty while
 * nothing has been priced" (`WorkOrderDetailSchema`), so this is a find rather than a
 * `[0]`: a work order that was re-priced from units into a template would otherwise
 * hand the quote fold an `extra` entry whose `project` is null. Null means "there is
 * no stored build-up", which the fold renders as the agreed total on its own.
 */
export function projectBreakdownOf(
  pricing: readonly ComputedPrice[] | undefined,
): ProjectBreakdownView | null {
  if (pricing === undefined) return null
  return pricing.find((one) => one.kind === 'project')?.project ?? null
}

/* -------------------------------------------------------------------------- money */

/**
 * The currency every figure on this project is in.
 *
 * Read off a `Money` the row already carries rather than off `work_order.currency`,
 * which is a plain `string` on the wire. Null when there is nothing priced at all —
 * a quote the studio still owes — and the page then prints no figure rather than a
 * zero in a currency nobody chose.
 */
export function currencyOf(project: ProjectRow): Currency | null {
  return project.work_order.total_amount?.currency ?? project.milestones[0]?.amount.currency ?? null
}

/**
 * The approved scope changes that are still to reach an invoice — the part of
 * `stillToInvoice` below that is **not** a milestone.
 *
 * Its own function because the hero has to be able to *name* it. Session 24 (R2-23)
 * found "Still to invoice $2,685.00 · Invoiced and paid so far $2,365.00" reading as a
 * partition of the $4,730.00 the milestone table directly below it sums to, when it is
 * in fact a partition of the $5,050.00 project to date: the $320.00 of scope approved
 * since the quote sits silently inside the first figure and was named nowhere near it.
 * A client comparing this page against the requests ledger's own
 * `WO-2026-008 · $4,730.00` had two totals for one project and no third line to
 * reconcile them, and the studio saw a third.
 *
 * Each figure keeps its owner — the ledger owns the project's own agreed total, the
 * fold owns the build-up to the project to date — and what was missing was the
 * sentence that says which of the two these two add up to. This is the amount that
 * sentence names.
 */
export function scopeStillToInvoice(
  project: ProjectRow,
  scopeChanges: readonly WorkOrderView[],
): Money | null {
  const currency = currencyOf(project)
  if (currency === null) return null

  const final = scopeChangeMilestone(project)
  const stillToCome =
    final === null || final.invoice_id !== null
      ? []
      : scopeChanges.map((one) => one.total_amount).filter((one): one is Money => one !== null)

  return sum(stillToCome, currency)
}

/**
 * `Still to invoice` — the milestones that have no invoice yet, plus the approved
 * scope changes that will ride on the final one. See this file's header for why
 * this is a permitted derivation rather than an invented fact.
 */
export function stillToInvoice(
  project: ProjectRow,
  scopeChanges: readonly WorkOrderView[],
): Money | null {
  const currency = currencyOf(project)
  if (currency === null) return null

  const uninvoiced = project.milestones.filter((one) => one.invoice_id === null)
  const scope = scopeStillToInvoice(project, scopeChanges)

  return sum([...uninvoiced.map((one) => one.amount), ...(scope === null ? [] : [scope])], currency)
}

/**
 * What this project has been invoiced for and has settled.
 *
 * Added up from the **milestone amounts** the table beside it prints, gated on
 * their own invoice having a `paid_at` — not from `InvoiceView.paid_amount`, and
 * for two reasons. The first is the rule: a permitted derivation's components have
 * to be *reachable on the page*, and `paid_amount` is not on this one. The second
 * is that the recording's own `paid_amount` is wrong for a milestone invoice —
 * exactly double the total on all three of them — which is reported to the lead as
 * a bug in the invoice read model rather than absorbed here.
 */
export function invoicedAndPaid(
  project: ProjectRow,
  invoices: readonly InvoiceView[],
  scopeChanges: readonly WorkOrderView[],
): Money | null {
  const currency = currencyOf(project)
  if (currency === null) return null

  const settled = project.milestones.filter((milestone) => {
    const issued = invoiceForMilestone(invoices, project, milestone)
    return issued !== null && issued.paid_at !== null
  })

  const final = scopeChangeMilestone(project)
  const finalInvoice = final === null ? null : invoiceForMilestone(invoices, project, final)
  const changesPaid =
    finalInvoice === null || finalInvoice.paid_at === null
      ? []
      : scopeChanges.map((one) => one.total_amount).filter((one): one is Money => one !== null)

  return sum([...settled.map((one) => one.amount), ...changesPaid], currency)
}

/**
 * What is invoiced, past its due date and still unpaid.
 *
 * `invoiceMachine` has no `overdue` state and should not: overdue is a fact about
 * today, not about the row (`ARCHITECTURE.md` §Money pipeline — settlement is
 * derived on every read). `today` is passed in so the page owns the one clock read,
 * and the amount is the milestone's own so the figure still reads down the table.
 */
export function overdue(
  project: ProjectRow,
  invoices: readonly InvoiceView[],
  today: string,
): Money | null {
  const currency = currencyOf(project)
  if (currency === null) return null

  const late = project.milestones.filter((milestone) => {
    const issued = invoiceForMilestone(invoices, project, milestone)
    return (
      issued !== null && issued.paid_at === null && issued.due_on !== null && issued.due_on < today
    )
  })

  return sum(
    late.map((one) => one.amount),
    currency,
  )
}

/**
 * `Project to date` — the agreed total plus every approved scope change.
 *
 * The same permitted derivation as `stillToInvoice`, and it lives inside the
 * `<details>` that summarises it: the fixed total and each change's own amount are
 * rows of that table, so it reconciles by reading down the column.
 */
export function projectToDate(
  project: ProjectRow,
  scopeChanges: readonly WorkOrderView[],
): Money | null {
  const currency = currencyOf(project)
  const total = project.work_order.total_amount
  if (currency === null || total === null) return null
  return sum(
    [
      total,
      ...scopeChanges.map((one) => one.total_amount).filter((one): one is Money => one !== null),
    ],
    currency,
  )
}

/* -------------------------------------------------------------------------- prose */

/**
 * `1, 2 and 3` — the English list the answer sentence needs.
 *
 * Here rather than in a component because it is the one piece of the sentence that
 * is not a value: `client/projects.html` writes "Milestones 1 and 2 are delivered
 * and paid", and a page that wrote `.join(', ')` would print "Milestones 1, 2".
 */
export function inWords(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? ''
  const last = items[items.length - 1] ?? ''
  return `${items.slice(0, -1).join(', ')} and ${last}`
}
